import { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionItem,
  Button,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Progress,
  Spinner,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tabs,
  useDisclosure
} from '@nextui-org/react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { CATALOG } from '../data/widgets-catalog';
import { useAuth } from '../hooks/useAuth';
import RobloxBinding from '../components/RobloxBinding';
import AuditLog from '../components/AuditLog';

const R2_BASE = 'https://storage.tikcontrol.live/';

// Plan quotas — mirror de cf-worker/src/api/files.js:42-59.
// Todos los planes premium_* y performance_* = 5 GB; free = 100 MB.
const GB = 1024 * 1024 * 1024;
const PLAN_QUOTAS = {
  free: 100 * 1024 * 1024,
  premium: 5 * GB,
  performance: 5 * GB
};

const PLAN_LABELS = {
  premium_internal: 'Premium interno',
  premium_ambassador: 'Premium embajador',
  premium_marketing: 'Premium Marketing',
  premium_trial: 'Premium prueba'
};

function planTier(plan) {
  if (!plan || plan === 'free') return 'free';
  const p = String(plan).toLowerCase();
  if (p.startsWith('premium')) return 'premium';
  if (p.startsWith('performance')) return 'performance';
  return 'free';
}

function quotaFor(plan) {
  return PLAN_QUOTAS[planTier(plan)] || PLAN_QUOTAS.free;
}

function formatBytes(n) {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(2)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatTs(value) {
  if (!value) return '—';
  let d;
  if (typeof value === 'number') d = new Date(value);
  else if (typeof value?.toDate === 'function') d = value.toDate();
  else if (typeof value === 'string') d = new Date(value);
  else return '—';
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function formatDate(value) {
  if (!value) return '—';
  let d;
  if (typeof value === 'number') d = new Date(value);
  else if (typeof value?.toDate === 'function') d = value.toDate();
  else if (typeof value === 'string') d = new Date(value);
  else if (typeof value?._seconds === 'number') d = new Date(value._seconds * 1000);
  else return '—';
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

function effectivePlanEnd(user) {
  return user?.stripeCurrentPeriodEnd || user?.planEnd || null;
}

function formatPlan(plan) {
  if (!plan) return 'free';
  if (PLAN_LABELS[plan]) return PLAN_LABELS[plan];
  return String(plan).replace(/_/g, ' ').toLowerCase();
}

function formatAppVersion(value) {
  const version = String(value || '').trim();
  return version ? `v${version}` : 'sin version';
}

// Campos "técnicos" que ya mostramos en otras secciones — los ocultamos del
// dump de identidad para no duplicar.
const HIDE_IDENTITY = new Set([
  'ADMIN',
  'TIKTOK_MANAGER',
  'plan',
  'planEnd',
  'stripeCurrentPeriodEnd',
  'email',
  'tiktokUsername',
  'tiktokNumericId',
  'displayName',
  'createdAt',
  'updatedAt',
  'appVersion',
  'appVersionLastSeenAt',
  'photoURL'
]);

function partitionFirestoreFields(data) {
  const identity = [];
  const roles = [];
  const other = [];
  for (const [k, v] of Object.entries(data || {})) {
    if (k === 'agencyRoles' || k.startsWith('OWNER_') || k.startsWith('MANAGER_') || k.startsWith('SUPEROWNER_')) {
      roles.push([k, v]);
    } else if (HIDE_IDENTITY.has(k)) {
      // Ya lo pintamos arriba.
    } else if (typeof v === 'object' && v !== null) {
      other.push([k, v]);
    } else {
      identity.push([k, v]);
    }
  }
  identity.sort(([a], [b]) => a.localeCompare(b));
  other.sort(([a], [b]) => a.localeCompare(b));
  return { identity, roles, other };
}

export default function UserDetail() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const { canAccess, isAdmin, loading: authLoading } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  if (!authLoading && !canAccess) {
    return <Navigate to="/users" replace />;
  }

  // Drill-down de un perfil cloud concreto
  const profileModal = useDisclosure();
  const [profileDetail, setProfileDetail] = useState(null);
  const [profileDetailLoading, setProfileDetailLoading] = useState(false);
  const [profileDetailError, setProfileDetailError] = useState(null);
  const [profileTab, setProfileTab] = useState('overview');
  const profileLinkModal = useDisclosure();
  const [profileLink, setProfileLink] = useState(null);
  const [profileLinkLoadingId, setProfileLinkLoadingId] = useState(null);
  const [profileLinkError, setProfileLinkError] = useState(null);

  function handleProfileModalOpenChange(open) {
    if (open) {
      profileModal.onOpen();
    } else {
      profileModal.onClose();
      setProfileDetail(null);
      setProfileDetailError(null);
      setProfileDetailLoading(false);
      setProfileTab('overview');

      // Defensivo: NextUI 2.6 a veces se olvida de restaurar overflow/
      // pointer-events y de quitar aria-hidden de la raíz cuando el Modal
      // convive con Accordion + Tabs. Limpio a mano al cerrar.
      setTimeout(() => {
        try {
          document.body.style.overflow = '';
          document.body.style.pointerEvents = '';
          document.documentElement.style.overflow = '';
          for (const el of document.querySelectorAll('[aria-hidden="true"]')) {
            // Sólo quitamos aria-hidden del root/app; no tocamos modales
            // realmente abiertos (que tendrían children de NextUI).
            if (el.id === 'root' || el.tagName === 'BODY' || el.tagName === 'HTML') {
              el.removeAttribute('aria-hidden');
            }
          }
        } catch {}
      }, 50);
    }
  }

  async function openProfileDetail(profileId) {
    setProfileDetail({ id: profileId });
    setProfileDetailError(null);
    setProfileTab('overview');
    profileModal.onOpen();
    setProfileDetailLoading(true);
    try {
      const res = await apiFetch(
        `/api/backoffice/users/${encodeURIComponent(uid)}/profiles/${encodeURIComponent(profileId)}`
      );
      setProfileDetail(res.profile);
    } catch (e) {
      setProfileDetailError(e.message);
    } finally {
      setProfileDetailLoading(false);
    }
  }

  async function generateProfileImportLink(profileId) {
    setProfileLink(null);
    setProfileLinkError(null);
    setProfileLinkLoadingId(profileId);
    try {
      const res = await apiFetch(
        `/api/backoffice/users/${encodeURIComponent(uid)}/profiles/${encodeURIComponent(profileId)}/import-link`,
        { method: 'POST' }
      );
      setProfileLink(res);
      profileLinkModal.onOpen();
      try {
        await navigator.clipboard.writeText(res.code || res.link || '');
      } catch (_) {}
    } catch (e) {
      setProfileLinkError(e.message);
      profileLinkModal.onOpen();
    } finally {
      setProfileLinkLoadingId(null);
    }
  }

  function loadProfile() {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiFetch(`/api/backoffice/users/${encodeURIComponent(uid)}/profile`)
      .then((res) => {
        if (!cancelled) setProfile(res);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }

  useEffect(() => {
    return loadProfile();
  }, [uid]);

  if (loading) {
    return (
      <div className="flex justify-center p-12">
        <Spinner label="Cargando perfil…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-4">
        <Button size="sm" variant="flat" onPress={() => navigate('/users')}>
          ← Volver a Users
        </Button>
        <div className="rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
          {error}
        </div>
      </div>
    );
  }

  const fs = profile?.firestore?.data || {};
  const roles = profile?.roles || {};
  const d1 = profile?.d1 || {};
  const r2 = profile?.r2 || {};
  const { identity, other } = partitionFirestoreFields(fs);

  // Storage "efectivo": R2 = used real, plan de Firestore → quota. La tabla
  // D1 storage_usage se queda desincronizada (sólo se actualiza vía el
  // endpoint /api/files), así que la usamos sólo como diagnóstico.
  const storage = {
    used: r2.totalSize || 0,
    quota: quotaFor(fs.plan),
    fileCount: r2.fileCount || 0,
    plan: fs.plan || 'free',
    tier: planTier(fs.plan)
  };
  const usagePct = storage.quota > 0 ? (storage.used / storage.quota) * 100 : 0;

  const catalogByKey = new Map(CATALOG.map((w) => [w.key, w]));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <Button size="sm" variant="flat" onPress={() => navigate('/users')}>
          ← Volver a Users
        </Button>
        <Button
          size="sm"
          variant="flat"
          onPress={loadProfile}
          isLoading={loading}
        >
          Refrescar
        </Button>
      </div>

      {isAdmin && <RobloxBinding uid={uid} />}
      {/* Header */}
      <div className="rounded-lg border border-default-200 bg-content1 p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-2xl font-semibold">
              {fs.tiktokUsername ? `@${fs.tiktokUsername}` : fs.email || uid}
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-default-500">
              {fs.email && <span>{fs.email}</span>}
              <code className="text-xs text-default-400">{uid}</code>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Chip size="sm" variant="flat">
                plan: {formatPlan(fs.plan)}
              </Chip>
              <Chip size="sm" variant="flat">
                fin: {formatDate(effectivePlanEnd(fs))}
              </Chip>
              <Chip size="sm" color="primary" variant="flat">
                app: {formatAppVersion(fs.appVersion)}
              </Chip>
              <Chip size="sm" variant="flat">
                ultimo inicio: {formatTs(fs.appVersionLastSeenAt)}
              </Chip>
              {roles.ADMIN && (
                <Chip size="sm" color="warning" variant="flat">ADMIN</Chip>
              )}
              {roles.TIKTOK_MANAGER && (
                <Chip size="sm" color="secondary" variant="flat">TIKTOK_MANAGER</Chip>
              )}
              {roles.OWNER.map((a) => (
                <Chip key={`o-${a}`} size="sm" color="primary" variant="flat">
                  OWNER {a}
                </Chip>
              ))}
              {roles.MANAGER.map((a) => (
                <Chip key={`m-${a}`} size="sm" variant="flat">
                  MANAGER {a}
                </Chip>
              ))}
              {/* Marcas antiguas OWNER_/MANAGER_ sin entrada en agencyRoles:
                  no dan acceso (el propio usuario podía ponérselas). */}
              {(roles.LEGACY_ONLY || []).map((k) => (
                <Chip key={`l-${k}`} size="sm" color="danger" variant="flat" className="line-through" title="Marca antigua sin efecto (no está en agencyRoles)">
                  {k}
                </Chip>
              ))}
            </div>
          </div>
          {fs.tiktokUsername && (
            <Button
              as="a"
              href={`https://www.tiktok.com/@${fs.tiktokUsername}`}
              target="_blank"
              rel="noreferrer"
              size="sm"
              variant="flat"
            >
              Ver en TikTok ↗
            </Button>
          )}
        </div>
      </div>

      {/* Overview cards */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="Widgets configurados" value={d1.configs?.length ?? 0} />
        <StatCard label="Perfiles cloud" value={d1.profiles?.length ?? 0} />
        <StatCard
          label="Version app"
          value={formatAppVersion(fs.appVersion)}
          sub={`ultimo inicio ${formatTs(fs.appVersionLastSeenAt)}`}
        />
        <StatCard
          label="Storage usado"
          value={formatBytes(storage.used)}
          sub={`${formatBytes(storage.quota)} quota · ${usagePct.toFixed(1)}%`}
        />
        <StatCard
          label="Archivos en R2"
          value={`${storage.fileCount}${r2.truncated ? '+' : ''}`}
          sub={`prefijo ${r2.prefix}`}
        />
      </div>

      {/* Storage progress bar */}
      {storage.quota > 0 && (
        <div className="rounded-lg border border-default-200 bg-content1 p-4">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-sm">
            <div className="font-medium">Storage usage</div>
            <div className="flex flex-wrap items-center gap-2 text-default-500">
              <span>
                {formatBytes(storage.used)} / {formatBytes(storage.quota)}
              </span>
              <Chip
                size="sm"
                variant="flat"
                color={storage.tier === 'free' ? 'default' : 'warning'}
              >
                {storage.plan}
              </Chip>
              {d1.storage &&
                (d1.storage.used !== storage.used ||
                  d1.storage.plan !== storage.plan) && (
                  <Chip size="sm" variant="flat" color="warning" title="D1 storage_usage fuera de sync">
                    D1 stale
                  </Chip>
                )}
            </div>
          </div>
          <Progress
            value={Math.min(100, usagePct)}
            color={usagePct > 90 ? 'danger' : 'primary'}
            size="sm"
          />
          {d1.storage &&
            (d1.storage.used !== storage.used || d1.storage.plan !== storage.plan) && (
              <p className="mt-2 text-xs text-default-400">
                D1 <code>storage_usage</code> marca {formatBytes(d1.storage.used)} /{' '}
                {formatBytes(d1.storage.quota)} · plan {d1.storage.plan} ·{' '}
                {d1.storage.fileCount} archivos (sólo se actualiza al subir/borrar
                vía la app — R2 es la fuente real).
              </p>
            )}
        </div>
      )}

      <Accordion
        selectionMode="multiple"
        defaultExpandedKeys={['widgets', 'profiles']}
        variant="splitted"
      >
        {/* Historial de cambios hechos desde el backoffice (se carga al abrirlo) */}
        <AccordionItem
          key="audit"
          title="Historial de cambios"
          subtitle="Quién le dio o quitó la licencia, cambió su rol o editó la cuenta"
        >
          <AuditLog uid={uid} pageSize={10} />
        </AccordionItem>

        {/* Widgets */}
        <AccordionItem
          key="widgets"
          title={
            <span>
              Widgets configurados{' '}
              <span className="text-default-400">· {d1.configs?.length || 0}</span>
            </span>
          }
        >
          {d1.configs?.length ? (
            <Table aria-label="Widgets" removeWrapper isStriped>
              <TableHeader>
                <TableColumn>Widget</TableColumn>
                <TableColumn>Título</TableColumn>
                <TableColumn>Tier</TableColumn>
                <TableColumn>Tamaños</TableColumn>
                <TableColumn>Actualizado</TableColumn>
              </TableHeader>
              <TableBody>
                {d1.configs.map((c) => {
                  const cat = catalogByKey.get(c.widget);
                  return (
                    <TableRow key={c.widget}>
                      <TableCell>
                        <code className="text-xs">{c.widget}</code>
                      </TableCell>
                      <TableCell>
                        {cat ? (
                          <span>{cat.title}</span>
                        ) : (
                          <span className="text-default-400">
                            (fuera del catálogo)
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {cat ? (
                          <Chip
                            size="sm"
                            variant="flat"
                            color={cat.tier === 'premium' ? 'warning' : 'success'}
                          >
                            {cat.tier}
                          </Chip>
                        ) : (
                          <span className="text-default-400">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-default-500">
                          cfg {formatBytes(c.config_size)} · state{' '}
                          {formatBytes(c.state_size)} · data{' '}
                          {formatBytes(c.data_size)}
                        </span>
                      </TableCell>
                      <TableCell>{formatTs(c.updated_at)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-default-500">Sin widgets configurados.</p>
          )}
        </AccordionItem>

        {/* Cloud profiles */}
        <AccordionItem
          key="profiles"
          title={
            <span>
              Perfiles cloud{' '}
              <span className="text-default-400">· {d1.profiles?.length || 0}</span>
            </span>
          }
        >
          {d1.profiles?.length ? (
            <Table aria-label="Perfiles cloud" removeWrapper isStriped>
              <TableHeader>
                <TableColumn>Nombre</TableColumn>
                <TableColumn>ID</TableColumn>
                <TableColumn>Tamaño</TableColumn>
                <TableColumn>Creado</TableColumn>
                <TableColumn>Actualizado</TableColumn>
                <TableColumn>Locked until</TableColumn>
                <TableColumn align="end">Acciones</TableColumn>
              </TableHeader>
              <TableBody>
                {d1.profiles.map((p) => (
                  <TableRow key={p.profile_id}>
                    <TableCell>{p.name || '—'}</TableCell>
                    <TableCell>
                      <code className="text-xs">{p.profile_id}</code>
                    </TableCell>
                    <TableCell>{formatBytes(p.data_size)}</TableCell>
                    <TableCell>{formatTs(p.created_at)}</TableCell>
                    <TableCell>{formatTs(p.updated_at)}</TableCell>
                    <TableCell>
                      {p.locked_until ? (
                        <Chip size="sm" color="warning" variant="flat">
                          {formatTs(p.locked_until)}
                        </Chip>
                      ) : (
                        <span className="text-default-400">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="flat"
                          color="primary"
                          onPress={() => generateProfileImportLink(p.profile_id)}
                          isLoading={profileLinkLoadingId === p.profile_id}
                        >
                          Link
                        </Button>
                        <Button
                          size="sm"
                          variant="flat"
                          onPress={() => openProfileDetail(p.profile_id)}
                        >
                          Ver
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-default-500">Sin perfiles cloud.</p>
          )}
        </AccordionItem>

        {/* Puntos / Batallas / Regalos */}
        <AccordionItem key="gameplay" title="Puntos / Batallas / Regalos">
          <div className="flex flex-col gap-3 text-sm">
            <div>
              <strong>Puntos:</strong>{' '}
              {d1.points
                ? `${d1.points.usersCount} usuarios rastreados · actualizado ${formatTs(d1.points.updated_at)}`
                : 'Sin datos.'}
            </div>
            <div>
              <strong>Batallas:</strong>{' '}
              {d1.battles ? (
                <span>
                  {d1.battles.stats?.wins || 0}W /{' '}
                  {d1.battles.stats?.losses || 0}L /{' '}
                  {d1.battles.stats?.ties || 0}T · {d1.battles.historyCount}{' '}
                  en historial · {formatTs(d1.battles.updated_at)}
                </span>
              ) : (
                'Sin datos.'
              )}
            </div>
            <div>
              <strong>Gift catalog:</strong>{' '}
              {d1.giftCatalog
                ? `${d1.giftCatalog.itemCount} items · ${formatTs(d1.giftCatalog.updated_at)}`
                : 'Sin datos.'}
            </div>
          </div>
        </AccordionItem>

        {/* R2 files sample */}
        <AccordionItem
          key="r2"
          title={
            <span>
              R2 users-data{' '}
              <span className="text-default-400">
                · {r2.fileCount || 0} archivos · {formatBytes(r2.totalSize || 0)}
              </span>
            </span>
          }
        >
          {r2.sample?.length ? (
            <>
              <p className="mb-2 text-xs text-default-500">
                Primeras {r2.sample.length} entradas bajo <code>{r2.prefix}</code>
                {r2.truncated && ' (listado truncado a 2000)'}
              </p>
              <Table aria-label="R2 sample" removeWrapper isStriped>
                <TableHeader>
                  <TableColumn>Key</TableColumn>
                  <TableColumn>Tamaño</TableColumn>
                  <TableColumn>Subido</TableColumn>
                  <TableColumn align="end">Acciones</TableColumn>
                </TableHeader>
                <TableBody>
                  {r2.sample.map((f) => (
                    <TableRow key={f.key}>
                      <TableCell>
                        <code className="text-xs">{f.key}</code>
                      </TableCell>
                      <TableCell>{formatBytes(f.size)}</TableCell>
                      <TableCell>{formatTs(f.uploaded)}</TableCell>
                      <TableCell>
                        <div className="flex justify-end">
                          <Button
                            size="sm"
                            variant="flat"
                            as="a"
                            href={R2_BASE + f.key}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Abrir
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          ) : (
            <p className="text-sm text-default-500">Sin archivos en R2.</p>
          )}
        </AccordionItem>

        {/* Otros campos Firestore */}
        <AccordionItem
          key="identity"
          title={
            <span>
              Otros campos Firestore{' '}
              <span className="text-default-400">· {identity.length + other.length}</span>
            </span>
          }
        >
          {identity.length === 0 && other.length === 0 ? (
            <p className="text-sm text-default-500">Sin campos adicionales.</p>
          ) : (
            <div className="flex flex-col gap-3">
              {identity.length > 0 && (
                <Table aria-label="Campos escalares" removeWrapper isStriped>
                  <TableHeader>
                    <TableColumn>Campo</TableColumn>
                    <TableColumn>Valor</TableColumn>
                  </TableHeader>
                  <TableBody>
                    {identity.map(([k, v]) => (
                      <TableRow key={k}>
                        <TableCell>
                          <code className="text-xs">{k}</code>
                        </TableCell>
                        <TableCell>
                          {typeof v === 'boolean'
                            ? v
                              ? 'true'
                              : 'false'
                            : v === null || v === undefined
                            ? '—'
                            : String(v)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {other.length > 0 && (
                <pre className="max-h-[40vh] overflow-auto rounded bg-default-100 p-3 text-xs">
                  {JSON.stringify(Object.fromEntries(other), null, 2)}
                </pre>
              )}
            </div>
          )}
        </AccordionItem>

        {/* Raw dump */}
        <AccordionItem key="raw" title="Dump JSON completo">
          <pre className="max-h-[60vh] overflow-auto rounded bg-default-100 p-3 text-xs">
            {JSON.stringify(profile, null, 2)}
          </pre>
        </AccordionItem>
      </Accordion>

      {profileModal.isOpen && (
        <ProfileDetailModal
          key={profileDetail?.id || 'new'}
          isOpen={true}
          onOpenChange={handleProfileModalOpenChange}
          detail={profileDetail}
          loading={profileDetailLoading}
          error={profileDetailError}
          activeTab={profileTab}
          setActiveTab={setProfileTab}
        />
      )}

      <Modal isOpen={profileLinkModal.isOpen} onClose={profileLinkModal.onClose}>
        <ModalContent>
          <ModalHeader>Codigo de importacion</ModalHeader>
          <ModalBody>
            {profileLinkError ? (
              <div className="rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
                {profileLinkError}
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <div className="rounded-md border border-default-200 bg-default-100 p-4 text-center font-mono text-2xl font-semibold">
                  {profileLink?.code || '-'}
                </div>
                <p className="text-sm text-default-500">
                  En la app: Perfiles - Importar - pega este codigo. Expira en {profileLink?.expiresInDays || 30} dias.
                </p>
                {profileLink?.link && (
                  <code className="break-all rounded bg-default-100 p-2 text-xs text-default-500">
                    {profileLink.link}
                  </code>
                )}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            {profileLink?.code && (
              <Button
                color="primary"
                variant="flat"
                onPress={() => navigator.clipboard.writeText(profileLink.code)}
              >
                Copiar codigo
              </Button>
            )}
            <Button variant="flat" onPress={profileLinkModal.onClose}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}

function ProfileDetailModal({
  isOpen,
  onOpenChange,
  detail,
  loading,
  error,
  activeTab,
  setActiveTab
}) {
  // El blob guardado en D1 es el objeto profile completo:
  //   { id, name, createdAt, updatedAt, createdBy, data: {...} }
  // Por lo que actions/events/_assets viven en `detail.data.data`, no en
  // `detail.data` directamente. Desenvolvemos una capa cuando detectamos
  // el wrapper. Si alguien guardó sólo el contenido, el fallback funciona.
  const outer = detail?.data || {};
  const isWrapped =
    outer &&
    typeof outer === 'object' &&
    outer.data &&
    typeof outer.data === 'object' &&
    ('id' in outer || 'name' in outer || 'createdAt' in outer || 'updatedAt' in outer);
  const data = isWrapped ? outer.data : outer;
  const actions = Array.isArray(data.actions) ? data.actions : [];
  const events = Array.isArray(data.events) ? data.events : [];

  // Widgets del perfil: deducidos de _assets (overlay v2 + widget configs).
  const widgetInfo = useMemo(() => computeProfileWidgets(data), [data]);

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="5xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader>
          <div className="flex flex-col">
            <span>{detail?.name || 'Perfil cloud'}</span>
            <span className="text-xs font-normal text-default-400">
              {detail?.id}
            </span>
          </div>
        </ModalHeader>
        <ModalBody>
          {loading ? (
            <div className="flex justify-center p-8">
              <Spinner label="Cargando perfil…" />
            </div>
          ) : error ? (
            <div className="rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
              {error}
            </div>
          ) : detail?.parseError ? (
            <div className="rounded-md border border-warning-200 bg-warning-50 p-3 text-sm text-warning-700">
              No se pudo parsear el blob del perfil: {detail.parseError}
            </div>
          ) : (
            <Tabs
              selectedKey={activeTab}
              onSelectionChange={(k) => setActiveTab(String(k))}
              aria-label="Secciones del perfil"
            >
              <Tab key="overview" title="Resumen">
                <div className="mt-2 flex flex-col gap-3">
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    <StatCard label="Acciones" value={actions.length} />
                    <StatCard label="Eventos" value={events.length} />
                    <StatCard
                      label="Widgets"
                      value={widgetInfo.total}
                      sub={`${widgetInfo.overlayCount} en overlay · ${widgetInfo.configCount} con config`}
                    />
                    <StatCard
                      label="Tamaño"
                      value={formatBytes(detail?.data_size || 0)}
                      sub={`actualizado ${formatTs(detail?.updated_at)}`}
                    />
                  </div>
                  {detail?.locked_until && (
                    <div className="rounded-md border border-warning-200 bg-warning-50 p-3 text-sm text-warning-700">
                      Perfil bloqueado hasta {formatTs(detail.locked_until)}{' '}
                      (periodo de gracia tras downgrade).
                    </div>
                  )}
                  <div className="rounded-md border border-default-200 bg-content1 p-3 text-sm">
                    <div className="mb-1 font-medium">Estructura del blob</div>
                    <div className="flex flex-col gap-1 text-xs text-default-500">
                      <div>
                        <strong>data keys:</strong>{' '}
                        {widgetInfo.topKeys?.length
                          ? widgetInfo.topKeys.map((k) => (
                              <code key={k} className="mr-1">
                                {k}
                                {Array.isArray(data[k])
                                  ? `[${data[k].length}]`
                                  : ''}
                              </code>
                            ))
                          : '—'}
                      </div>
                      <div>
                        <strong>_assets:</strong>{' '}
                        {widgetInfo.assetsKeys?.length
                          ? widgetInfo.assetsKeys.map((k) => (
                              <code key={k} className="mr-1">
                                {k}
                              </code>
                            ))
                          : 'sin _assets (los overlays/widget configs no están sincronizados en este perfil — el usuario puede que no haya activado cloud sync o no se haya disparado triggerProfileAssetSync tras editar)'}
                      </div>
                    </div>
                  </div>
                </div>
              </Tab>

              <Tab
                key="actions"
                title={`Acciones (${actions.length})`}
              >
                <ActionEventTable items={actions} emptyText="Sin acciones en este perfil." />
              </Tab>

              <Tab key="events" title={`Eventos (${events.length})`}>
                <ActionEventTable items={events} emptyText="Sin eventos en este perfil." />
              </Tab>

              <Tab
                key="widgets"
                title={`Widgets (${widgetInfo.total})`}
              >
                <WidgetList info={widgetInfo} />
              </Tab>

              <Tab key="raw" title="JSON">
                <pre className="max-h-[60vh] overflow-auto rounded bg-default-100 p-3 text-xs">
                  {JSON.stringify(data, null, 2)}
                </pre>
              </Tab>
            </Tabs>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="flat" onPress={() => onOpenChange?.(false)}>
            Cerrar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function ActionEventTable({ items, emptyText }) {
  const [expanded, setExpanded] = useState(null);

  if (!items.length) {
    return <p className="py-4 text-sm text-default-500">{emptyText}</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      <Table aria-label="Lista" removeWrapper isStriped>
        <TableHeader>
          <TableColumn>Nombre</TableColumn>
          <TableColumn>Tipo</TableColumn>
          <TableColumn>Trigger / Gift</TableColumn>
          <TableColumn>Enabled</TableColumn>
          <TableColumn>ID</TableColumn>
          <TableColumn align="end">Acciones</TableColumn>
        </TableHeader>
        <TableBody>
          {items.map((it) => {
            const trigger = extractTrigger(it);
            const type =
              it.type || it.kind || it.actionType || it.eventType || '—';
            const enabled = it.enabled !== false && it.disabled !== true;
            return (
              <TableRow key={it.id || JSON.stringify(it).slice(0, 40)}>
                <TableCell>
                  <span className="font-medium">
                    {it.name || it.label || it.title || '—'}
                  </span>
                </TableCell>
                <TableCell>
                  <Chip size="sm" variant="flat">{type}</Chip>
                </TableCell>
                <TableCell>
                  <span className="text-xs text-default-500">{trigger || '—'}</span>
                </TableCell>
                <TableCell>
                  {enabled ? (
                    <Chip size="sm" variant="flat" color="success">on</Chip>
                  ) : (
                    <Chip size="sm" variant="flat" color="default">off</Chip>
                  )}
                </TableCell>
                <TableCell>
                  <code className="text-xs text-default-400">{it.id || '—'}</code>
                </TableCell>
                <TableCell>
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      variant="light"
                      onPress={() => setExpanded(expanded?.id === it.id ? null : it)}
                    >
                      {expanded?.id === it.id ? 'Ocultar' : 'JSON'}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {expanded && (
        <pre className="max-h-[40vh] overflow-auto rounded bg-default-100 p-3 text-xs">
          {JSON.stringify(expanded, null, 2)}
        </pre>
      )}
    </div>
  );
}

function WidgetList({ info }) {
  const catalogByKey = new Map(CATALOG.map((w) => [w.key, w]));
  const allEntries = info.entries;
  const [layoutFilter, setLayoutFilter] = useState('vertical');

  const counts = useMemo(() => {
    const acc = { all: allEntries.length, vertical: 0, horizontal: 0, other: 0 };
    for (const e of allEntries) {
      if (e.layout === 'vertical') acc.vertical++;
      else if (e.layout === 'horizontal') acc.horizontal++;
      else acc.other++;
    }
    return acc;
  }, [allEntries]);

  const entries = useMemo(() => {
    if (layoutFilter === 'all') return allEntries;
    if (layoutFilter === 'other') {
      return allEntries.filter(
        (e) => e.layout !== 'vertical' && e.layout !== 'horizontal'
      );
    }
    return allEntries.filter((e) => e.layout === layoutFilter);
  }, [allEntries, layoutFilter]);

  if (!allEntries.length) {
    return (
      <div className="flex flex-col gap-3 py-4 text-sm">
        <p className="text-default-500">
          No se detectaron widgets en el blob. Posibles causas:
        </p>
        <ul className="ml-5 list-disc text-default-500">
          <li>
            Los archivos de overlay (<code>{'{profileId}_v2.json'}</code>) y
            widget configs no se han sincronizado — la sync sólo se dispara
            al llamar <code>saveProfile()</code> o{' '}
            <code>triggerProfileAssetSync()</code>.
          </li>
          <li>
            El usuario aún no ha activado cloud sync o está en plan con el
            perfil locked.
          </li>
          <li>
            El shape del blob es distinto al esperado — revisa la tab JSON.
          </li>
        </ul>
        {(info.topKeys?.length || info.assetsKeys?.length) && (
          <div className="rounded-md border border-default-200 bg-content1 p-3 text-xs text-default-500">
            <div>
              <strong>Top-level keys:</strong>{' '}
              {info.topKeys?.length ? info.topKeys.join(', ') : '—'}
            </div>
            <div>
              <strong>_assets:</strong>{' '}
              {info.assetsKeys?.length ? info.assetsKeys.join(', ') : '—'}
            </div>
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <Tabs
        size="sm"
        selectedKey={layoutFilter}
        onSelectionChange={(k) => setLayoutFilter(String(k))}
        aria-label="Filtrar por vista"
      >
        <Tab key="vertical" title={`Vertical (${counts.vertical})`} />
        <Tab key="horizontal" title={`Horizontal (${counts.horizontal})`} />
        {counts.other > 0 && (
          <Tab key="other" title={`Otros (${counts.other})`} />
        )}
        <Tab key="all" title={`Todos (${counts.all})`} />
      </Tabs>
      {entries.length === 0 && (
        <div className="rounded-md border border-default-200 bg-content1 p-3 text-sm text-default-500">
          Sin elementos en la vista <strong>{layoutFilter}</strong>. Este perfil
          sólo ha configurado la otra orientación.
        </div>
      )}
      {entries.length > 0 && (
      <Table aria-label="Widgets del perfil" removeWrapper isStriped>
        <TableHeader>
          <TableColumn>Nombre</TableColumn>
          <TableColumn>Tipo</TableColumn>
          <TableColumn>Vista</TableColumn>
          <TableColumn>Widget catálogo</TableColumn>
          <TableColumn>Fuente</TableColumn>
          <TableColumn>Detalle</TableColumn>
        </TableHeader>
        <TableBody>
          {entries.map((e) => {
            const cat = e.widgetKey ? catalogByKey.get(e.widgetKey) : null;
            return (
              <TableRow key={e.uniqueKey}>
                <TableCell>
                  {e.name ? (
                    <span className="font-medium">{e.name}</span>
                  ) : (
                    <span className="text-default-400 italic">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <Chip size="sm" variant="flat">{e.type || '—'}</Chip>
                </TableCell>
                <TableCell>
                  {e.layout ? (
                    <Chip
                      size="sm"
                      variant="flat"
                      color={
                        e.layout === 'vertical'
                          ? 'primary'
                          : e.layout === 'horizontal'
                          ? 'secondary'
                          : 'default'
                      }
                    >
                      {e.layout}
                    </Chip>
                  ) : (
                    <span className="text-default-400">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {cat ? (
                    <span>
                      {cat.title}{' '}
                      <code className="text-xs text-default-400">{cat.key}</code>
                    </span>
                  ) : e.widgetKey ? (
                    <code className="text-xs text-default-500">{e.widgetKey}</code>
                  ) : (
                    <span className="text-default-400">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <Chip size="sm" variant="flat">
                    {e.sourceLabel || e.source}
                  </Chip>
                </TableCell>
                <TableCell>
                  <span className="text-xs text-default-500">{e.detail || '—'}</span>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      )}
    </div>
  );
}

function extractTrigger(item) {
  // Intenta adivinar el trigger/target del item con los campos comunes
  // observados en el repo (renderer/ae/*).
  if (!item || typeof item !== 'object') return null;
  if (item.trigger) {
    if (typeof item.trigger === 'string') return item.trigger;
    if (typeof item.trigger === 'object') {
      return (
        item.trigger.type ||
        item.trigger.name ||
        item.trigger.gift ||
        item.trigger.event ||
        JSON.stringify(item.trigger).slice(0, 60)
      );
    }
  }
  if (item.giftId || item.gift) return `gift: ${item.gift || item.giftId}`;
  if (item.keyword) return `keyword: ${item.keyword}`;
  if (item.diamonds) return `${item.diamonds} diamantes`;
  if (item.action) return `→ ${item.action}`;
  return null;
}

// Extrae los elementos de un blob tipo overlay-layout.
// Devuelve [[layoutName, elementsArray], ...] sólo para layouts explícitos
// (vertical / horizontal). Ignora arrays flat `elements` del root porque los
// archivos `overlay-vertical-*.json` / `overlay-horizontal-*.json` duplican
// lo que ya viene en `_assets.overlayV2.{vertical,horizontal}.elements`.
function extractLayoutElements(blob) {
  if (!blob || typeof blob !== 'object') return [];
  const layouts = [];
  if (blob.vertical?.elements) layouts.push(['vertical', blob.vertical.elements]);
  if (blob.horizontal?.elements) layouts.push(['horizontal', blob.horizontal.elements]);
  return layouts;
}

// Construye un row normalizado desde un elemento crudo del overlay editor.
function elementToEntry(el, { source, sourceLabel, layout, index }) {
  const e = el || {};
  const type = e.type || e.kind || e.elementType || '(unknown)';
  const name = e.name || e.label || e.title || null;
  const widgetKey =
    e.widget ||
    e.widgetKey ||
    e.widgetId ||
    e.widgetName ||
    e.config?.widget ||
    e.config?.widgetKey ||
    e.data?.widget ||
    e.data?.widgetKey ||
    null;
  const zIndex =
    e.z ?? e.zIndex ?? e.layer ?? e.z_index ?? e.zOrder ?? null;

  return {
    uniqueKey: `${source}|${layout}|${e.id || index}`,
    source,
    sourceLabel,
    layout,
    index,
    name,
    type,
    widgetKey,
    zIndex,
    elementId: e.id || null,
    detail: `${layout}${zIndex != null ? ` · L${zIndex}` : ''}${
      e.id ? ` · id ${e.id}` : ''
    }`
  };
}

function computeProfileWidgets(data) {
  const entries = [];
  if (!data)
    return {
      total: 0,
      entries: [],
      overlayCount: 0,
      configCount: 0,
      layoutCounts: {},
      topKeys: [],
      assetsKeys: []
    };

  // 1) Overlay v2 principal (userData/overlay-configs/{id}_v2.json)
  const overlayCandidates = [
    ['_assets.overlayV2', 'overlay v2', data._assets?.overlayV2],
    ['overlayV2', 'overlay v2', data.overlayV2],
    ['overlay', 'overlay legacy', data.overlay],
    ['root', 'root', data.vertical || data.horizontal ? data : null]
  ].filter(([, , v]) => v && typeof v === 'object');

  for (const [sourcePath, sourceLabel, overlay] of overlayCandidates) {
    for (const [layoutName, elements] of extractLayoutElements(overlay)) {
      for (let i = 0; i < elements.length; i++) {
        entries.push(
          elementToEntry(elements[i], {
            source: sourcePath,
            sourceLabel,
            layout: layoutName,
            index: i
          })
        );
      }
    }
  }

  // 2) Widget configs por-perfil — archivos aparte, cada uno puede contener
  // su propio layout (overlay-vertical/horizontal-*.json) o una config sin
  // elementos (gift-battle_v2-*.json).
  const cfgs = data._assets?.widgetConfigs || data.widgetConfigs || {};
  for (const [fname, content] of Object.entries(cfgs || {})) {
    const base = String(fname).replace(/-[a-z0-9]+\.json$/i, '');
    const inner = extractLayoutElements(content);

    if (inner.length === 0) {
      // No es un layout — emitir sólo el "file meta" row
      entries.push({
        uniqueKey: `cfg|${fname}`,
        source: 'asset config',
        sourceLabel: base,
        layout: null,
        name: null,
        type: 'config',
        widgetKey: base || fname,
        zIndex: null,
        elementId: null,
        detail: `${fname}${
          typeof content === 'object'
            ? ` · ${Object.keys(content || {}).length} campos`
            : ''
        }`
      });
      continue;
    }

    // Es un layout — emitir una row por elemento
    for (const [layoutName, elements] of inner) {
      for (let i = 0; i < elements.length; i++) {
        entries.push(
          elementToEntry(elements[i], {
            source: `asset:${base}`,
            sourceLabel: base,
            layout: layoutName,
            index: i
          })
        );
      }
    }
  }

  const overlayCount = entries.filter((e) => e.source === '_assets.overlayV2' || e.source === 'overlayV2').length;
  const configCount = entries.filter((e) => e.source === 'asset config' || String(e.source).startsWith('asset:')).length;

  // Conteo por layout (vertical/horizontal) agregado por fuente.
  const layoutCounts = {};
  for (const e of entries) {
    if (!e.layout) continue;
    const key = `${e.sourceLabel || e.source}:${e.layout}`;
    layoutCounts[key] = (layoutCounts[key] || 0) + 1;
  }

  const topKeys = Object.keys(data).sort();
  const assetsKeys = data._assets ? Object.keys(data._assets).sort() : [];

  return {
    total: entries.length,
    entries,
    overlayCount,
    configCount,
    layoutCounts,
    topKeys,
    assetsKeys
  };
}

function StatCard({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-default-200 bg-content1 p-4">
      <div className="text-xs uppercase tracking-wider text-default-500">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
      {sub && <div className="mt-1 text-xs text-default-500">{sub}</div>}
    </div>
  );
}
